import * as React from "react";
import {
  FieldPath,
  FieldValues,
  Path,
  PathValue,
  useController,
  useFormContext,
} from "react-hook-form";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { cva, VariantProps } from "class-variance-authority";
import { CheckIcon, CircleIcon } from "lucide-react";
import { cn } from "~/lib/utils";
import { Label } from "./label";

// ==================
// RadioGroup
// ==================

const RadioGroupContext = React.createContext({
  value: "",
});

type RadioGroupContextValue = React.ContextType<typeof RadioGroupContext>;

type RadioGroupProps = React.ComponentProps<typeof RadioGroupPrimitive.Root>;

function RadioGroup({ className, value, defaultValue, ...props }: RadioGroupProps) {
  const [internalValue, setInternalValue] = React.useState(defaultValue ?? "");

  const contextValue = React.useMemo<RadioGroupContextValue>(
    () => ({
      value: value ?? internalValue,
    }),
    [value, internalValue]
  );
  return (
    <RadioGroupContext.Provider value={contextValue}>
      <RadioGroupPrimitive.Root
        {...props}
        defaultValue={defaultValue}
        value={value ?? internalValue}
        data-slot="radio-group"
        className={cn("grid gap-3", className)}
        onValueChange={(value) => {
          setInternalValue(value);
          props.onValueChange?.(value);
        }}
      />
    </RadioGroupContext.Provider>
  );
}

// ==================
// RadioGroupIndicator
// ==================

const radioGroupIndicatorVariants = cva(
  [
    "group cursor-pointer border-gray-300 bg-white text-primary aspect-square size-4 shrink-0 rounded-full border shadow-xs transition-[color,box-shadow] outline-none",
    "focus-visible:border-neutral-500 focus-visible:ring-gray-500/50 focus-visible:ring-[3px]",
    "aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
    "disabled:cursor-not-allowed disabled:opacity-50",
  ],
  {
    variants: {
      variant: {
        default: "",
        check: "data-[state=checked]:bg-primary data-[state=checked]:border-primary",
        inverseDot: "data-[state=checked]:bg-primary data-[state=checked]:border-primary",
      },
    },
  }
);

const iconVariants = cva("absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2", {
  variants: {
    icon: {
      circle: "hidden",
      check: "hidden",
    },
    variant: {
      default: "fill-primary size-2",
      check: "stroke-white stroke-3 size-3",
      inverseDot: "fill-white size-2.5",
    },
  },
  defaultVariants: {
    variant: "default",
  },
  compoundVariants: [
    {
      icon: "circle",
      variant: "default",
      className: "block",
    },
    {
      icon: "circle",
      variant: "inverseDot",
      className: "block",
    },
    {
      icon: "check",
      variant: "check",
      className: "block",
    },
  ],
});

interface RadioGroupIndicatorProps
  extends Omit<React.ComponentProps<typeof RadioGroupPrimitive.Item>, "children">,
    VariantProps<typeof radioGroupIndicatorVariants> {}

function RadioGroupIndicator({ className, variant, ...props }: RadioGroupIndicatorProps) {
  return (
    <RadioGroupPrimitive.Item
      {...props}
      data-slot="radio-group-item"
      className={cn(radioGroupIndicatorVariants({ variant }), className)}
    >
      <RadioGroupPrimitive.Indicator
        data-slot="radio-group-indicator"
        className="relative flex items-center justify-center"
      >
        <CircleIcon className={cn(iconVariants({ variant, icon: "circle" }))} />
        <CheckIcon className={cn(iconVariants({ variant, icon: "check" }))} />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
}

// ==================
// RadioGroupItem
// ==================

const radioGroupItemVariants = cva("flex items-start gap-2 cursor-pointer w-fit relative", {
  variants: {
    selected: {
      true: "",
      false: "",
    },
    variant: {
      default: "",
      card: "border border-gray-300 rounded-lg p-2",
      imageCard: "border-2 border-gray-300 rounded-lg p-3 bg-white",
    },
  },
  compoundVariants: [
    {
      selected: true,
      variant: "card",
      className: "bg-primary/5 border-primary shadow-sm",
    },
    {
      selected: true,
      variant: "imageCard",
      className: "border-primary shadow-sm",
    },
  ],
});

const VariantMap: Record<
  NonNullable<VariantProps<typeof radioGroupItemVariants>["variant"]>,
  RadioGroupIndicatorProps["variant"]
> = {
  default: "default",
  card: "inverseDot",
  imageCard: "check",
};

interface RadioGroupItemProps<
  T extends FieldValues = FieldValues,
  P extends FieldPath<T> = FieldPath<T>
> extends Omit<RadioGroupIndicatorProps, "variant" | "value">,
    VariantProps<typeof radioGroupItemVariants> {
  value: PathValue<T, P>;
  label: React.ReactNode;
  description?: string;
  indicatorClassName?: string;
}

function RadioGroupItem<
  T extends FieldValues = FieldValues,
  P extends FieldPath<T> = FieldPath<T>
>({
  className,
  indicatorClassName,
  label,
  description,
  variant,
  ...indicatorProps
}: RadioGroupItemProps<T, P>) {
  const id = React.useId();
  const { value } = React.useContext(RadioGroupContext);

  return (
    <Label
      htmlFor={indicatorProps.id ?? id}
      className={cn(
        radioGroupItemVariants({ variant, selected: value === indicatorProps.value }),
        className
      )}
    >
      <RadioGroupIndicator
        {...indicatorProps}
        id={indicatorProps.id ?? id}
        className={indicatorClassName}
        variant={variant ? VariantMap[variant] : "default"}
      />
      <div className="cursor-pointer flex flex-col gap-1.5 items-start">
        {typeof label === "string" ? (
          <span className="font-medium text-sm text-gray-950 leading-none">{label}</span>
        ) : (
          label
        )}
        {!!description && (
          <span className="text-xs text-gray-600 justify-start leading-5">{description}</span>
        )}
      </div>
    </Label>
  );
}

interface ControlledRadioGroupProps<T extends FieldValues = FieldValues> extends RadioGroupProps {
  name: Path<T>;
}

function ControlledRadioGroup<T extends FieldValues = FieldValues>({
  name,
  ...props
}: ControlledRadioGroupProps<T>) {
  const { control } = useFormContext<T>();
  const { field } = useController({
    control,
    name,
  });

  return (
    <RadioGroup
      {...props}
      {...field}
      value={field.value as string}
      onValueChange={field.onChange}
    />
  );
}

export {
  RadioGroup,
  type RadioGroupProps,
  ControlledRadioGroup,
  type ControlledRadioGroupProps,
  RadioGroupIndicator,
  type RadioGroupIndicatorProps,
  RadioGroupItem,
  type RadioGroupItemProps,
};
